// FILE: EnvironmentsSection.logic.ts
// Purpose: Pure helpers behind the Environments settings panel — row ordering,
//          who is still eligible for one, disk sizes, and the preview URL —
//          kept apart from the component so they can be tested without React.
// Exports: `sortEnvironments`, `eligibleOwners`, `formatBytes`, `previewUrl`,
//          `environmentStatusLabel`.

import type { AdminEnvironmentRow } from "../../auth/EnvironmentsClient";

/**
 * Broken first, then alphabetical by owner.
 *
 * An admin opens this panel either to hand someone an environment or because
 * one is not working; `failed` at the top serves the second case without
 * costing the first anything, since the list is short by construction (at most
 * one live environment per person per project).
 */
const statusRank = (row: AdminEnvironmentRow): number =>
  row.status === "failed" ? 0 : row.status === "provisioning" ? 1 : 2;

export function sortEnvironments(
  rows: ReadonlyArray<AdminEnvironmentRow>,
  ownerName: (row: AdminEnvironmentRow) => string,
): ReadonlyArray<AdminEnvironmentRow> {
  return rows.toSorted((left, right) => {
    const byStatus = statusRank(left) - statusRank(right);
    if (byStatus !== 0) return byStatus;
    return ownerName(left).localeCompare(ownerName(right), undefined, { sensitivity: "base" });
  });
}

export interface OwnerCandidate {
  readonly userId: string;
  readonly name: string;
  readonly email: string;
}

export interface ProjectAssignment {
  readonly projectId: string;
  readonly permissions: ReadonlyArray<string>;
}

/**
 * Whether this person's threads will actually land in the environment.
 *
 * Provisioning creates the worktree; `p.env.use` is what makes threads use it.
 * They are separate on purpose — a resource and a grant — but an admin who does
 * the first and forgets the second gets no error and no effect, just an
 * environment nobody ever enters. Surfacing it is cheaper than explaining it.
 */
export function ownerCanUseEnvironment(
  assignments: ReadonlyArray<ProjectAssignment> | undefined,
  projectId: string,
): boolean {
  const assignment = (assignments ?? []).find((entry) => entry.projectId === projectId);
  return assignment !== undefined && assignment.permissions.includes("p.env.use");
}

/**
 * Users who could still be given an environment on this project.
 *
 * The server enforces one live environment per `(user, project)` and answers
 * 409 otherwise; filtering here is only so the admin is not offered a choice
 * that is going to be refused. A `failed` environment still holds the slot —
 * its half-built worktree has to be destroyed first — so its owner is excluded
 * exactly like a working one.
 */
export function eligibleOwners(
  users: ReadonlyArray<OwnerCandidate>,
  existing: ReadonlyArray<AdminEnvironmentRow>,
): ReadonlyArray<OwnerCandidate> {
  // Only personal environments hold a slot: a project one belongs to nobody,
  // and its null owner must not exclude anyone from the list.
  const taken = new Set(
    existing
      .filter((row) => row.deletedAt === null && row.kind === "personal" && row.userId !== null)
      .map((row) => row.userId as string),
  );
  return users
    .filter((user) => !taken.has(user.userId))
    .toSorted((left, right) =>
      left.name.localeCompare(right.name, undefined, { sensitivity: "base" }),
    );
}

const UNITS = ["B", "KB", "MB", "GB", "TB"] as const;

/**
 * Disk size for a human scanning a table.
 *
 * `null` means the dump file could not be stat'd, which is not the same as
 * zero — an environment whose snapshots weigh nothing and one whose size is
 * unknown must not read alike.
 */
export function formatBytes(bytes: number | null): string {
  if (bytes === null) return "—";
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${UNITS[unit]}`;
}

/**
 * The environment's preview address, or `null` while it has none.
 *
 * Both halves are required: a hostname without a port is an allocation that
 * never completed, and linking to it would send the admin to a dead page.
 */
export function previewUrl(row: AdminEnvironmentRow): string | null {
  if (row.devHostname === null || row.devPort === null) return null;
  return `https://${row.devHostname}`;
}

export function environmentStatusLabel(status: AdminEnvironmentRow["status"]): string {
  switch (status) {
    case "ready":
      return "Listo";
    case "provisioning":
      return "Preparando…";
    case "failed":
      return "Falló";
  }
}
